import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import PropdocsModal from '../../../components/modal/PropdocsModal';
import { useNotifications } from '../../../notifications';
import { shareChecklistTemplateToWorkspaces } from '../../../api/checklist-queries';
import SubmitButton from '../../../components/submit-button';
import styles from './modals.module.scss';

interface ShareChecklistTemplateModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  template: {
    id: string;
    name: string;
    sharedToAffiliatedWorkspaces: boolean;
  } | null;
  refetch: () => void;
}

export const ShareChecklistTemplateModal: React.FC<ShareChecklistTemplateModalProps> = ({
  show,
  setShow,
  template,
  refetch,
}) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [shareTemplate, { loading: sharing }] = useMutation(shareChecklistTemplateToWorkspaces);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleShare = async () => {
    if (!template) return;

    setIsSubmitting(true);

    try {
      const result = await shareTemplate({
        variables: { templateId: template.id },
      });

      if (result.data?.shareChecklistTemplateToWorkspaces?.errors?.length) {
        notifyError({
          message: {
            id: 'checklist-template.share-error',
            defaultMessage: result.data.shareChecklistTemplateToWorkspaces.errors[0] || 'Failed to share template',
          },
        });
        setIsSubmitting(false);
        return;
      }

      const workspaceCount = result.data?.shareChecklistTemplateToWorkspaces?.affiliatedWorkspaceCount || 0;
      const isShared = result.data?.shareChecklistTemplateToWorkspaces?.isShared || false;

      if (isShared) {
        notifySuccess({
          message: {
            id: 'checklist-template.shared',
            defaultMessage: `Template shared to ${workspaceCount} affiliated workspace${workspaceCount === 1 ? '' : 's'}`,
          },
        });
      } else {
        notifySuccess({
          message: {
            id: 'checklist-template.unshared',
            defaultMessage: 'Template sharing removed',
          },
        });
      }

      await refetch();
      setShow(false);
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist-template.share-error',
          defaultMessage: 'An error occurred while sharing the template',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!template) return null;

  const isAlreadyShared = template.sharedToAffiliatedWorkspaces;

  return (
    <PropdocsModal
      size="md"
      show={show}
      setShow={() => setShow(false)}
      title={<FormattedMessage id="checklist-template.share-title" defaultMessage="Share Checklist Template" />}
    >
      <>
        <div className={styles.modalBody}>
          {isAlreadyShared ? (
            <>
              <p>
                <FormattedMessage
                  id="checklist-template.unshare-description"
                  defaultMessage="Remove sharing for {templateName}?"
                  values={{ templateName: <strong>{template.name}</strong> }}
                />
              </p>
              <p className={styles.warningText}>
                <FormattedMessage
                  id="checklist-template.unshare-warning"
                  defaultMessage="This template will no longer be available to affiliated workspaces."
                />
              </p>
            </>
          ) : (
            <>
              <p>
                <FormattedMessage
                  id="checklist-template.share-description"
                  defaultMessage="Share {templateName} with your affiliated workspaces?"
                  values={{ templateName: <strong>{template.name}</strong> }}
                />
              </p>
              <p className={styles.infoText}>
                <FormattedMessage
                  id="checklist-template.share-info"
                  defaultMessage="This template will become available to all affiliated workspaces in your organization."
                />
              </p>
            </>
          )}
        </div>

        <div className={styles.modalActions}>
          <Button variant="link" onClick={() => setShow(false)} disabled={isSubmitting}>
            <FormattedMessage id="cta.cancel" defaultMessage="Cancel" />
          </Button>
          <SubmitButton
            label={
              isAlreadyShared ? (
                <FormattedMessage id="cta.remove-sharing" defaultMessage="Remove Sharing" />
              ) : (
                <FormattedMessage id="cta.share" defaultMessage="Share" />
              )
            }
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
            onClick={handleShare}
          />
        </div>
      </>
    </PropdocsModal>
  );
};

export default ShareChecklistTemplateModal;
